import React, { useState, useEffect } from 'react';
import AchievementImage from '../../Achievements/AchievementImage';
import adminService from '../../../services/adminService';
import './AchievementManagementTable.scss';

const AchievementManagementTable = ({ userId, username }) => {
  const [achievements, setAchievements] = useState([]);
  const [userAchievements, setUserAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState({});

  const gameNameMap = {
    'slay-the-spire': 'Slay the Spire',
    'bible-books': 'Bible Books',
    'subnautica-creatures': 'Subnautica Creatures',
    'aoe2-units': 'Age of Empires II'
  };

  const categoryNameMap = {
    'daily-streak': 'Daily Streak',
    'user-groups': 'User Groups',
    'total-wins': 'Total Wins'
  };

  useEffect(() => {
    if (userId) {
      fetchUserAchievements();
    }
  }, [userId]);

  const fetchUserAchievements = async () => {
    setLoading(true);
    setError('');
    
    try {
      const data = await adminService.getUserAchievements(userId);
      setAchievements(data.achievements);
      setUserAchievements(data.userAchievements);
    } catch (error) {
      console.error('Error fetching user achievements:', error);
      setError('Failed to load achievements');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAchievement = async (achievementId, isUnlocked) => {
    const action = isUnlocked ? 'revoke' : 'grant';
    setActionLoading(prev => ({ ...prev, [achievementId]: true }));
    
    try {
      if (isUnlocked) {
        await adminService.revokeAchievement(userId, achievementId);
      } else {
        await adminService.grantAchievement(userId, achievementId);
      }

      // Refresh achievements after successful action
      await fetchUserAchievements();
    } catch (error) {
      console.error(`Error ${action}ing achievement:`, error);
      setError(`Failed to ${action} achievement`);
    } finally {
      setActionLoading(prev => ({ ...prev, [achievementId]: false }));
    }
  };

  const isAchievementUnlocked = (achievementId) => {
    return userAchievements.some(ua => ua.achievement_id === achievementId);
  };

  const isManuallyManageable = (achievement) => {
    // User Groups category achievements can be manually managed
    // Also allow manual management of any achievement for admin purposes
    return achievement.category === 'user-groups' || true;
  };

  const groupAchievementsByCategory = () => {
    const grouped = {};
    achievements.forEach(achievement => {
      if (!grouped[achievement.category]) {
        grouped[achievement.category] = [];
      }
      grouped[achievement.category].push(achievement);
    });
    
    // Sort achievements within each category by sort_order
    Object.keys(grouped).forEach(category => {
      grouped[category].sort((a, b) => a.sort_order - b.sort_order);
    });
    
    return grouped;
  };

  if (loading) {
    return (
      <div className="achievement-management-table">
        <div className="loading">Loading achievements...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="achievement-management-table">
        <div className="error">{error}</div>
        <button onClick={fetchUserAchievements} className="retry-btn">
          Retry
        </button>
      </div>
    );
  }

  const groupedAchievements = groupAchievementsByCategory();

  return (
    <div className="achievement-management-table">
      <div className="achievement-management-header">
        <h3>Achievement Management</h3>
      </div>

      {Object.entries(groupedAchievements).map(([category, categoryAchievements]) => (
        <div key={category} className="achievement-category">
          <h4 className="category-title">{categoryNameMap[category] || category}</h4>
          
          <div className="achievement-grid">
            {categoryAchievements.map(achievement => {
              const isUnlocked = isAchievementUnlocked(achievement.id);
              const isLoading = actionLoading[achievement.id];
              const canManage = isManuallyManageable(achievement);
              
              return (
                <div 
                  key={achievement.id} 
                  className={`achievement-item ${isUnlocked ? 'unlocked' : 'locked'}`}
                >
                  <div className="achievement-image">
                    <AchievementImage 
                      filename={achievement.image_filename}
                      alt={achievement.name}
                      placeholderImage="/achievements/placeholder/default.png"
                    />
                    {!isUnlocked && (
                      <div className="lock-overlay">
                        <img src="/achievements/locked/Locked.png" alt="Locked" />
                      </div>
                    )}
                  </div>
                  
                  <div className="achievement-info">
                    <h5 className="achievement-name">{achievement.name}</h5>
                    <p className="achievement-description">{achievement.description}</p>
                    
                    {achievement.game_type && (
                      <div className="achievement-game">
                        <span className="game-badge">
                          {gameNameMap[achievement.game_type] || achievement.game_type}
                        </span>
                      </div>
                    )}
                    
                    {achievement.threshold && (
                      <div className="achievement-threshold">
                        Threshold: {achievement.threshold}
                      </div>
                    )}
                  </div>
                  
                  <div className="achievement-controls">
                    <div className="achievement-status">
                      <span className={`status-badge ${isUnlocked ? 'unlocked' : 'locked'}`}>
                        {isUnlocked ? 'Unlocked' : 'Locked'}
                      </span>
                    </div>
                    
                    {canManage && (
                      <button
                        className={`toggle-btn ${isUnlocked ? 'revoke' : 'grant'}`}
                        onClick={() => handleToggleAchievement(achievement.id, isUnlocked)}
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <div className="spinner"></div>
                        ) : (
                          isUnlocked ? 'Revoke' : 'Grant'
                        )}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default AchievementManagementTable;